import * as yup from "yup";
import { EMAIL_WITH_TLD_REGEX, US_PHONE_REGEX, NON_US_PHONE_REGEX, NAME_REGEX } from "../../../../constants/ValidationConstants";

function isAllZeros(value) {
    if (!value) return false;
    const digits = value.replace(/\D/g, '');
    return digits.length === 10 && /^0+$/.test(digits);
}

function isFirstThreeZeros(value) {
    if (!value) return false;
    const digits = value.replace(/\D/g, '');
    return digits.length === 10 && digits.substring(0, 3) === "000";
}

function isUsCountryCode(countryCode) {
    return !countryCode || countryCode === "+1";
}

export const validationSchema = yup.object({
    firstName: yup
        .string()
        .required("Please enter your first name")
        .max(50)
        .matches(NAME_REGEX, "First name must contain only letters, no special characters"),
    middleName: yup.string()
        .nullable()
        .test("middle-name-check", "Middle name must contain only letters, no special characters.", value => {
            return !value || NAME_REGEX.test(value);
        }),
    lastName: yup
        .string()
        .required("Please enter your last name")
        .max(50)
        .matches(NAME_REGEX, "Last name must contain only letters, no special characters"),
    suffix: yup.string().optional(),
    businessEmail: yup.string()
        .email("Please enter a valid email address")
        .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address")
        .required("Please enter a valid email address"),
    mobilePhone: yup.string()
        .required("Please enter a valid phone number")
        .when("mobilePhoneCountryCode", {
            is: countryCode => isUsCountryCode(countryCode),
            then: schema => schema.matches(US_PHONE_REGEX, "Please enter a valid 10-digit US phone number"),
            otherwise: schema => schema.matches(NON_US_PHONE_REGEX, "Please enter a valid phone number")
        })
        .test("not-all-zeros", "Phone number cannot be all zeros", value => !isAllZeros(value))
        .test("first-three-not-zeros", "First three digits cannot be all zeros", value => !isFirstThreeZeros(value)),
    businessPhone: yup.string()
        .required("Please enter a valid phone number")
        .when("businessPhoneCountryCode", {
            is: countryCode => isUsCountryCode(countryCode),
            then: schema => schema.matches(US_PHONE_REGEX, "Please enter a valid 10-digit US phone number"),
            otherwise: schema => schema.matches(NON_US_PHONE_REGEX, "Please enter a valid phone number")
        })
        .test("not-all-zeros", "Phone number cannot be all zeros", value => !isAllZeros(value))
        .test("first-three-not-zeros", "First three digits cannot be all zeros", value => !isFirstThreeZeros(value)),
    offshoreResident: yup.bool().optional(),
    mobilePhoneCountryCode: yup.string().when("offshoreResident", {

        is: isOffshoreResident => isOffshoreResident === true || isOffshoreResident === "true",
        then: schema => schema.required("Please select a country code."),
        otherwise: schema => schema.optional()
    }),
    businessPhoneCountryCode: yup.string().when("offshoreResident", {
        is: isOffshoreResident => isOffshoreResident === true || isOffshoreResident === "true",
        then: schema => schema.required("Please select a country code."),
        otherwise: schema => schema.optional()
    }),
    textOptIn: yup.bool().optional(),
    role: yup.string().required("Please select your agency role"),
    npn: yup.string()
        .test("length-check", "Please enter a valid NPN.", npn => npn == null || npn.length === 0 || (npn?.length >= 5 && npn?.length <= 9))
        .when("role", (role, schema) =>
            ["csr", "authorized-appointment-rep", "other-employee"].some(r => r == role) ?
                schema.optional() :
                schema.required("Please enter a valid NPN.")),
    agencyName: yup
        .string()
        .required("Please enter the Agency Name."),
    soleProprietor: yup.string().required("Please select Yes or No."),
    fein: yup.string()
        .transform(fein => fein.replace("-", ""))
        .when("soleProprietor", {
            is: soleProprietor => soleProprietor === "false",
            then: schema => schema
                .length(9, "Please enter a valid FEIN for your agency.")
                .required("Please enter a valid FEIN for your agency.")
        }),
    lobCommercial: yup.bool(),
    lobPersonal: yup.bool(),
    interestedLob: yup.bool().test("lob-check", "Please select at least one line of business.", (_, context) => {
        return context.parent.lobCommercial || context.parent.lobPersonal;
    }),
    geicoAppointedStates: yup.array()
        .typeError("Please select at least one state.")
        .min(1, "Please select at least one state."),
    captiveAgent: yup.bool().when("role", {
        is: "principal",
        then: schema => schema.required("Please select Yes or No."),
        otherwise: schema => schema.optional().nullable()
    }),
    captiveCarrier: yup.string().when(["captiveAgent", "role"], {
        is: (captiveAgent, role) => captiveAgent === true && role === "principal",
        then: schema => schema.required("Please select a carrier."),
        otherwise: schema => schema.optional().nullable()
    }),
    captiveCarrierOther: yup.string().when(["captiveAgent", "captiveCarrier", "role"], {
        is: (captiveAgent, captiveCarrier, role) => captiveAgent === true && captiveCarrier === "other" && role === "principal",
        then: schema => schema.required("Please enter the carrier."),
        otherwise: schema => schema.optional().nullable()
    }),
    activeEoPolicy: yup.string().when("role", {
        is: "principal",
        then: schema => schema.required("Please select Yes or No."),
        otherwise: schema => schema.optional().nullable()
    }),
    intendEoPolicy: yup.string().default("").when(["activeEoPolicy", "role"], {
        is: (activeEoPolicy, role) => activeEoPolicy === "false" && role === "principal",
        then: schema => schema.required("Please select Yes or No."),
        otherwise: schema => schema.optional().nullable()
    })
});